/**
 * Fortschritts-Store — 1:1 zum Web-Vertrag:
 *  - lokal profil-namensraumig (`ki-kurs-fortschritt::<profil-id>`),
 *  - optimistisches Signal,
 *  - Remote-State (GET /api/progress/state) wird beim Laden hineingemerged.
 * content_key spiegelt die Backend-Konvention `module:block:item_type:item_id`.
 * Event-Typen wie Web: Fragen `checked`/`unchecked`, Kapitel `completed`/`reset`.
 */
import { signal } from "@preact/signals";
import { endpoints } from "../api/endpoints";
import type { ProgressStateItem } from "../api/types";
import { activeProfileId } from "./session";
import { loadCertificateStatus } from "./completion";

/** content_key → erledigt. */
export const doneKeys = signal<Set<string>>(new Set());
export const loadedCourse = signal<string | null>(null);

const localKey = () => `ki-kurs-fortschritt::${activeProfileId()}`;

export function contentKey(
  moduleId: string | null,
  blockId: string | null,
  itemType: string,
  itemId: string,
): string {
  return `${moduleId ?? "_"}:${blockId ?? "_"}:${itemType}:${itemId}`;
}

function parseKey(key: string): { m: string; b: string; type: string; id: string } {
  const [m, b, type, ...rest] = key.split(":");
  return { m, b, type, id: rest.join(":") };
}

function readLocal(): Set<string> {
  try {
    const raw = localStorage.getItem(localKey());
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function writeLocal(set: Set<string>): void {
  try {
    localStorage.setItem(localKey(), JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function isDoneRow(item: ProgressStateItem): boolean {
  return Boolean(item.completed || item.checked);
}

export async function loadProgress(courseId: string): Promise<void> {
  // Lokalen Stand sofort zeigen …
  const merged = readLocal();
  doneKeys.value = new Set(merged);
  // … dann Remote dazu mergen.
  try {
    const res = await endpoints.progressState(courseId);
    for (const item of res.items ?? []) {
      if (isDoneRow(item)) merged.add(item.content_key);
    }
  } catch {
    /* offline o. ä. — lokaler Stand bleibt */
  }
  doneKeys.value = new Set(merged);
  writeLocal(merged);
  loadedCourse.value = courseId;
}

export function isDone(key: string): boolean {
  return doneKeys.value.has(key);
}

/* --- Unterkapitel (Abschnitte, Task F) — 1:1 zum Web-Vertrag:
   item_type "subchapter", item_id "sub-<slug>"; gelesen = Abschnitt explizit
   ODER ganzes Kapitel früher abgeschlossen (Alt-Stand ohne Einzelmarken). --- */

export function subContentKey(moduleId: string, blockId: string, ukKey: string): string {
  return contentKey(moduleId, blockId, "subchapter", `sub-${ukKey}`);
}

export function isSubRead(moduleId: string, blockId: string, ukKey: string): boolean {
  return (
    doneKeys.value.has(subContentKey(moduleId, blockId, ukKey)) ||
    doneKeys.value.has(contentKey(moduleId, blockId, "chapter", "chapter"))
  );
}

/** „Weiter" markiert den aktuellen Abschnitt als gelesen (idempotent, best-effort remote). */
export function markSubRead(courseId: string, moduleId: string, blockId: string, ukKey: string): void {
  const key = subContentKey(moduleId, blockId, ukKey);
  if (doneKeys.value.has(key)) return;
  const next = new Set(doneKeys.value);
  next.add(key);
  doneKeys.value = next;
  writeLocal(next);
  void endpoints
    .progressEvent({
      course_id: courseId,
      module_id: moduleId,
      block_id: blockId,
      item_id: `sub-${ukKey}`,
      item_type: "subchapter",
      event_type: "completed",
      checked: true,
      completed: true,
    })
    .catch(() => {
      /* offline — lokaler Stand bleibt, Remote merged später */
    });
}

/**
 * Erledigt-Status umschalten (optimistisch, lokal persistiert, Event ans Backend).
 * itemType "question" → checked/unchecked; "chapter" → completed/reset.
 */
export async function toggleDone(opts: {
  courseId: string;
  moduleId: string | null;
  blockId: string | null;
  itemId: string;
  itemType: string;
}): Promise<void> {
  const key = contentKey(opts.moduleId, opts.blockId, opts.itemType, opts.itemId);
  const next = new Set(doneKeys.value);
  const willBeDone = !next.has(key);
  if (willBeDone) next.add(key);
  else next.delete(key);
  doneKeys.value = next;
  writeLocal(next);

  const isChapter = opts.itemType === "chapter";
  const eventType = willBeDone ? (isChapter ? "completed" : "checked") : (isChapter ? "reset" : "unchecked");
  try {
    await endpoints.progressEvent({
      course_id: opts.courseId,
      module_id: opts.moduleId,
      block_id: opts.blockId,
      item_id: opts.itemId,
      item_type: opts.itemType,
      event_type: eventType,
      checked: willBeDone,
      completed: willBeDone,
    });
    // Nach Kapitel-Abschluss pruefen, ob das Grossziel erreicht ist (Web-Parität).
    if (isChapter && willBeDone) void loadCertificateStatus(opts.courseId);
  } catch {
    // Rollback bei Fehler.
    const revert = new Set(doneKeys.value);
    if (willBeDone) revert.delete(key);
    else revert.add(key);
    doneKeys.value = revert;
    writeLocal(revert);
  }
}

/** Reines Fortschritts-Event (kein Toggle) — z. B. chapter_opened, stream_*, Medien. */
export function sendProgressEvent(payload: {
  courseId: string;
  moduleId: string | null;
  blockId: string | null;
  itemId: string;
  itemType: string;
  eventType: string;
  positionSeconds?: number;
  durationSeconds?: number | null;
  completed?: boolean;
}): void {
  void endpoints
    .progressEvent({
      course_id: payload.courseId,
      module_id: payload.moduleId,
      block_id: payload.blockId,
      item_id: payload.itemId,
      item_type: payload.itemType,
      event_type: payload.eventType,
      position_seconds: payload.positionSeconds,
      duration_seconds: payload.durationSeconds ?? undefined,
      completed: payload.completed,
    })
    .catch(() => {
      /* best-effort */
    });
}

/**
 * Fortschritt zurücksetzen: lokalen Key leeren UND für jedes erledigte Item ein
 * reset/unchecked-Event senden, damit der Remote-Stand nicht zurückkommt.
 */
export function resetProgress(courseId?: string): void {
  const current = [...doneKeys.value];
  doneKeys.value = new Set();
  try {
    localStorage.removeItem(localKey());
  } catch {
    /* ignore */
  }
  loadedCourse.value = null;
  if (!courseId) return;
  for (const key of current) {
    const p = parseKey(key);
    sendProgressEvent({
      courseId,
      moduleId: p.m === "_" ? null : p.m,
      blockId: p.b === "_" ? null : p.b,
      itemId: p.id,
      itemType: p.type,
      eventType: p.type === "chapter" ? "reset" : "unchecked",
      completed: false,
    });
  }
}
